'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  BadgeCheck,
  CheckCircle2,
  Image as ImageIcon,
  LayoutTemplate,
  Loader2,
  Sparkles,
  Wand2,
} from 'lucide-react'
import {
  useTaskFlowStore,
  type TaskFlowPhase,
  type TaskFlowRequest,
} from '@/lib/task-flow/taskFlowStore'
import confetti from 'canvas-confetti'
import { useIsAdmin } from '@/hooks/useIsAdmin'

type FlyerMode = 'manual' | 'auto'

type FlyerTemplateItem = {
  template_id: number
  name: string
  label?: string | null
  description?: string
  prompt_base?: string
  placement_mode?: string
  aspect_ratio?: string
  status?: number
  base_image_url?: string | null
  overlay_image_url?: string | null
  mask_image_url?: string | null
  metadata_json?: any
}

type PortraitItem = {
  portrait_id: number
  image_url: string | null
  source_image_url?: string | null
  is_primary?: number
  provider?: string | null
  model?: string | null
  date_added?: string
}

type FlyerResult = {
  flyer_generation_id: number
  image_url: string | null
  status?: string
  provider?: string | null
  model?: string | null
  overlay_pass_used?: boolean
  date_added?: string
  template?: {
    template_id: number
    name: string
    description?: string
  }
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

function FlowShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border border-cyan-400/10 bg-white text-slate-900 dark:bg-[#030712] dark:text-cyan-50 md:rounded-[32px]">
      {children}
    </div>
  )
}

const DEMO_OVERLAY_FIELDS = [
  {
    key: 'name',
    label: 'Nombre',
    type: 'text',
    required: true,
    x: 50,
    y: 77,
    width: 80,
    fontSize: 34,
    fontFamily: 'Montserrat, Arial, sans-serif',
    fontWeight: 800,
    color: '#ffffff',
    align: 'center',
    textTransform: 'uppercase',
  },
  {
    key: 'date',
    label: 'Fecha',
    type: 'text',
    required: true,
    x: 50,
    y: 84,
    width: 80,
    fontSize: 20,
    fontFamily: 'Montserrat, Arial, sans-serif',
    fontWeight: 600,
    color: '#ffffff',
    align: 'center',
    textTransform: 'none',
  },
]

function proxiedImageUrl(src?: string | null) {
  if (!src) return ''
  return `/api/image/proxy?url=${encodeURIComponent(src)}`
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function StepCard({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-[32px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.92))] p-6 shadow-[0_20px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-cyan-400/14 dark:bg-[linear-gradient(180deg,rgba(10,18,34,0.96),rgba(5,11,22,0.92))] dark:shadow-[0_0_0_1px_rgba(34,211,238,0.03),0_20px_80px_rgba(0,0,0,0.35)]">
      <div className="text-[11px] uppercase tracking-[0.28em] text-cyan-700/70 dark:text-cyan-300/58">
        {eyebrow}
      </div>

      <div className="mt-3 text-3xl font-semibold tracking-tight text-center text-slate-900 dark:text-cyan-50">
        {title}
      </div>

      <div className="mt-3 max-w-2xl mx-auto text-center text-sm leading-relaxed text-slate-600 dark:text-cyan-100/58">
        {subtitle}
      </div>

      <div className="mt-6 flex flex-col items-center">{children}</div>
    </div>
  )
}

function LoadingRuntime() {
  return (
    <div className="flex min-h-[65vh] flex-col items-center justify-center text-center">
      <div className="relative mb-7">
        <div className="h-16 w-16 rounded-full border-2 border-cyan-400/20 border-t-cyan-400 animate-spin" />
        <div className="absolute inset-0 rounded-full bg-cyan-400/20 blur-2xl" />
      </div>

      <div className="text-2xl font-semibold text-slate-900 dark:text-cyan-50">
        Estamos creando algo para ti...
      </div>

      <div className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 dark:text-cyan-100/60">
        Estamos combinando tu retrato profesional con el template seleccionado para generar un flyer premium.
      </div>

      <div className="mt-7 grid w-full max-w-lg gap-3">
        {['Preparando template', 'Integrando retrato', 'Ajustando composición', 'Finalizando diseño'].map(
          (label, index) => (
            <div
              key={label}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-600 dark:border-cyan-400/10 dark:bg-cyan-400/[0.04] dark:text-cyan-100/65"
            >
              <div
                className={cn(
                  'h-2.5 w-2.5 rounded-full',
                  index === 3 ? 'bg-cyan-400 animate-pulse' : 'bg-cyan-400/40'
                )}
              />
              {label}
            </div>
          )
        )}
      </div>
    </div>
  )
}

function renderOverlayText(value: string, field: any) {
  let text = String(value || '')

  if (field.textTransform === 'uppercase') text = text.toUpperCase()
  if (field.textTransform === 'lowercase') text = text.toLowerCase()

  return text
}

export default function SynCreateFlyerFlow({
  request,
}: {
  request: TaskFlowRequest
  phase: TaskFlowPhase
}) {
  const resetTaskFlow = useTaskFlowStore((s) => s.resetTaskFlow)

  const { isAdmin, loading: adminLoading } = useIsAdmin()

  const ENABLE_FLYER_DEMO = adminLoading || Boolean(isAdmin)

  const FLYER_DEMO_DELAY_MS = 5000

  const FLYER_DEMO_IMAGE_URL =
    'https://unitedkin.org/images/users/9/1784152831506_222baf7ffe89.png'

  const sellerId = Number(request.payload?.sellerId || 0)
  const userId = Number(request.payload?.userId || 0)

  const requestedMode = String(request.payload?.mode || '').trim().toLowerCase()
  const autoModeFromPayload =
    requestedMode === 'auto' ||
    request.payload?.autoGenerate === true ||
    request.payload?.autoRun === true

  const configuredTemplateId = Number(request.payload?.templateId || 0)
  const configuredPortraitId = Number(request.payload?.portraitId || 0)

  const [mode, setMode] = useState<FlyerMode>(autoModeFromPayload ? 'auto' : 'manual')
  const [manualStep, setManualStep] = useState<1 | 2 | 3>(1)
  const [templateSearch, setTemplateSearch] = useState('')
  const [selectedLabelFilter, setSelectedLabelFilter] = useState('all')
  const [templates, setTemplates] = useState<FlyerTemplateItem[]>([])
  const [portraits, setPortraits] = useState<PortraitItem[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    configuredTemplateId || null
  )
  const [selectedPortraitId, setSelectedPortraitId] = useState<number | null>(
    configuredPortraitId || null
  )

  const [savedFinalUrl, setSavedFinalUrl] = useState<string | null>(null)
  const [savingFinal, setSavingFinal] = useState(false)

  const [loadingBootstrap, setLoadingBootstrap] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [flyerResult, setFlyerResult] = useState<FlyerResult | null>(null)

  const [showShareOptions, setShowShareOptions] = useState(false)

  const autoStartedRef = useRef(false)

  const [overlayValues, setOverlayValues] = useState<Record<string, string>>({
    name: '',
    date: '',
  })

  const [finalRenderedUrl, setFinalRenderedUrl] = useState<string | null>(null)
  const [renderingFinal, setRenderingFinal] = useState(false)

  const activeTemplateList = useMemo(
    () => templates.filter((t) => Number(t.status || 0) === 1),
    [templates]
  )

  const templateLabels = useMemo(() => {
    const unique = Array.from(
      new Set(
        activeTemplateList
          .map((t: any) => String(t.label || '').trim())
          .filter(Boolean)
      )
    )

    return unique.sort((a, b) => a.localeCompare(b))
  }, [activeTemplateList])

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()

    return activeTemplateList.filter((template: any) => {
      const name = String(template.name || '').toLowerCase()
      const description = String(template.description || '').toLowerCase()
      const label = String(template.label || '').toLowerCase()

      const matchesSearch =
        !q ||
        name.includes(q) ||
        description.includes(q) ||
        label.includes(q)

      const matchesLabel =
        selectedLabelFilter === 'all' ||
        String(template.label || '').trim() === selectedLabelFilter

      return matchesSearch && matchesLabel
    })
  }, [activeTemplateList, templateSearch, selectedLabelFilter])

  const selectedTemplate = useMemo(
    () => templates.find((t) => Number(t.template_id) === Number(selectedTemplateId)) || null,
    [templates, selectedTemplateId]
  )

  const selectedPortrait = useMemo(
    () => portraits.find((p) => Number(p.portrait_id) === Number(selectedPortraitId)) || null,
    [portraits, selectedPortraitId]
  )

  const primaryPortrait = useMemo(
    () => portraits.find((p) => Number(p.is_primary || 0) === 1) || portraits[0] || null,
    [portraits]
  )

  const overlayFields = useMemo(() => {
    const fields = selectedTemplate?.metadata_json?.overlay_fields
    return Array.isArray(fields) ? fields : []
  }, [selectedTemplate])

  function fireConfetti() {
    const count = 200
    const defaults = {
      origin: { y: 0.7 },
    }

    function fire(particleRatio: number, opts: any) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      })
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    })

    fire(0.2, {
      spread: 60,
    })

    fire(0.35, {
      spread: 100,
      decay: 0.91,
    })

    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
    })

    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    })
  }

  function handleTemplateSelect(templateId: number) {
    setSelectedTemplateId(templateId)
    setManualStep(2)
  }

  function handlePortraitSelect(portraitId: number) {
    setSelectedPortraitId(portraitId)
    setManualStep(3)
  }

  function getFlyerShareUrl() {
    return savedFinalUrl || finalRenderedUrl || flyerResult?.image_url || ''
  }

  async function copyFlyerLink() {
    const url = getFlyerShareUrl()
    if (!url) return

    await navigator.clipboard.writeText(url)
    setShowShareOptions(false)
  }

  function shareToFacebook() {
    const url = encodeURIComponent(getFlyerShareUrl())
    if (!url) return

    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  function shareToWhatsApp() {
    const url = encodeURIComponent(getFlyerShareUrl())
    if (!url) return

    window.open(
      `https://wa.me/?text=${url}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  async function saveFinalRenderedFlyer() {
    if (savedFinalUrl) return savedFinalUrl
    if (!finalRenderedUrl || !flyerResult?.flyer_generation_id) return null

    setSavingFinal(true)

    try {
      const res = await fetch('/api/creator-studio/flyer/save-final', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flyerGenerationId: flyerResult.flyer_generation_id,
          imageDataUrl: finalRenderedUrl,
          overlayValues,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo guardar el flyer final')
      }

      setSavedFinalUrl(json.url)
      return json.url
    } finally {
      setSavingFinal(false)
    }
  }

  async function completeOnboardingToolStep(meta?: Record<string, any>) {
    const moduleId = Number(request.payload?.onboardingStepId || 0)
    const currentSellerId = Number(request.payload?.sellerId || 0)

    if (!moduleId || !currentSellerId) return

    try {
      const res = await fetch('/api/dashboard/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId: currentSellerId,
          moduleId,
          meta: meta || null,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo completar el step')
      }
    } catch (err) {
      console.error('[SynCreateFlyerFlow] completeOnboardingToolStep error:', err)
    }
  }

  async function loadTemplates() {
    const res = await fetch('/api/creator-studio/flyer/templates', {
      cache: 'no-store',
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || 'No se pudieron cargar los templates')
    }

    const items = Array.isArray(json.items) ? json.items : []
    const activeItems = items.filter((item: FlyerTemplateItem) => Number(item.status || 0) === 1)

    setTemplates(activeItems)

    setSelectedTemplateId((prev) => {
      if (prev && activeItems.some((x: FlyerTemplateItem) => Number(x.template_id) === Number(prev))) {
        return prev
      }

      return activeItems[0]?.template_id || null
    })

    return activeItems
  }

  async function loadPortraits() {
    const res = await fetch('/api/creator-studio/portrait/list', {
      cache: 'no-store',
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || 'No se pudieron cargar los retratos')
    }

    const items = Array.isArray(json.items) ? json.items : []

    setPortraits(items)

    setSelectedPortraitId((prev) => {
      if (prev && items.some((x: PortraitItem) => Number(x.portrait_id) === Number(prev))) {
        return prev
      }

      const primary = items.find((x: PortraitItem) => Number(x.is_primary || 0) === 1)

      return primary?.portrait_id || items[0]?.portrait_id || null
    })

    return items
  }

  async function renderFinalFlyerWithOverlay() {
    if (!flyerResult?.image_url) return

    try {
      setRenderingFinal(true)

      const img = new Image()
      img.crossOrigin = 'anonymous'

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('No se pudo cargar el flyer'))
        img.src = proxiedImageUrl(flyerResult.image_url)
      })

      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('No canvas context')

      ctx.drawImage(img, 0, 0)

      for (const field of overlayFields) {
        const value = renderOverlayText(overlayValues[field.key], field)
        if (!value) continue

        const x = (field.x / 100) * canvas.width
        const y = (field.y / 100) * canvas.height
        const maxWidth = (field.width / 100) * canvas.width

        const previewWidth = 420
        const scale = canvas.width / previewWidth
        const fontSize = Number(field.fontSize || 32) * scale

        ctx.save()

        ctx.translate(x, y)
        ctx.rotate(((Number(field.rotation || 0) * Math.PI) / 180))

        ctx.font = `${field.fontWeight || 700} ${fontSize}px ${field.fontFamily || 'Arial'}`
        ctx.fillStyle = field.color || '#ffffff'
        ctx.textBaseline = 'middle'
        ctx.shadowColor = 'rgba(0,0,0,0.35)'
        ctx.shadowBlur = 10
        ctx.shadowOffsetY = 2

        const align = (field.align || 'center') as CanvasTextAlign
        ctx.textAlign = align

        let textX = 0

        if (align === 'left') {
          textX = -maxWidth / 2
        } else if (align === 'right') {
          textX = maxWidth / 2
        }

        ctx.fillText(value, textX, 0, maxWidth)

        ctx.restore()
      }

      const dataUrl = canvas.toDataURL('image/png', 0.95)
      setFinalRenderedUrl(dataUrl)
    } catch (e: any) {
      setError(e?.message || 'No se pudo renderizar el flyer final')
    } finally {
      setRenderingFinal(false)
    }
  }

  async function bootstrap() {
    try {
      setLoadingBootstrap(true)
      setError(null)

      const [templateItems, portraitItems] = await Promise.all([
        loadTemplates(),
        loadPortraits(),
      ])

      if (!templateItems.length) {
        setError('No hay templates activos para crear flyers.')
      }

      if (!portraitItems.length) {
        setError('Primero necesitas crear un retrato profesional.')
      }
    } catch (e: any) {
      setError(e?.message || 'Error cargando Flyer Flow')
    } finally {
      setLoadingBootstrap(false)
    }
  }

  const hasFiredConfettiRef = useRef(false)

  useEffect(() => {
    if (!flyerResult || hasFiredConfettiRef.current) return

    hasFiredConfettiRef.current = true
    fireConfetti()
  }, [flyerResult])

  useEffect(() => {
    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function generateFlyer(opts?: {
    templateId?: number | null
    portraitId?: number | null
    source?: string
  }) {
    const templateId = Number(opts?.templateId || selectedTemplateId || 0)
    const portraitId = Number(opts?.portraitId || selectedPortraitId || primaryPortrait?.portrait_id || 0)

    if (!templateId) {
      setError('Selecciona un template')
      return
    }

    if (!portraitId) {
      setError('No encontré un retrato profesional usable')
      return
    }

    try {
      setGenerating(true)
      setError(null)
      setFlyerResult(null)

      if (ENABLE_FLYER_DEMO) {
        await wait(FLYER_DEMO_DELAY_MS)

        const demoFlyer: FlyerResult = {
          flyer_generation_id: 999999,
          image_url: FLYER_DEMO_IMAGE_URL,
          status: 'ready',
          provider: 'demo',
          model: 'demo_flyer_mock',
          overlay_pass_used: false,
          date_added: new Date().toISOString(),
          template: selectedTemplate
            ? {
                template_id: selectedTemplate.template_id,
                name: selectedTemplate.name,
                description: selectedTemplate.description || '',
              }
            : undefined,
        }

        setFlyerResult(demoFlyer)

        window.dispatchEvent(new CustomEvent('dashboard-onboarding-refresh'))

        return
      }

      const res = await fetch('/api/creator-studio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tab: 'flyer',
          inputs: {
            templateId,
            portraitId,
            forceProfessionalize: 0,
            source: opts?.source || 'task_flow_flyer',
            userId,
            sellerId,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo generar el flyer')
      }

      const flyer = json?.generation?.flyer || json?.flyer || null

      if (!flyer?.flyer_generation_id) {
        throw new Error('No se recibió el flyer generado')
      }

      const normalizedFlyer: FlyerResult = {
        flyer_generation_id: Number(flyer.flyer_generation_id),
        image_url: flyer.image_url || null,
        status: flyer.status || 'ready',
        provider: flyer.provider || null,
        model: flyer.model || null,
        overlay_pass_used: Boolean(flyer.overlay_pass_used),
        date_added: flyer.date_added,
        template: flyer.template
          ? {
              template_id: Number(flyer.template.template_id),
              name: flyer.template.name || '',
              description: flyer.template.description || '',
            }
          : selectedTemplate
          ? {
              template_id: selectedTemplate.template_id,
              name: selectedTemplate.name,
              description: selectedTemplate.description || '',
            }
          : undefined,
      }

      setFlyerResult(normalizedFlyer)

      await completeOnboardingToolStep({
        source: 'flyer_flow',
        toolKey: 'flyer_creator',
        flyerGenerationId: normalizedFlyer.flyer_generation_id,
        templateId,
        portraitId,
        status: normalizedFlyer.status || 'ready',
      })

      window.dispatchEvent(new CustomEvent('dashboard-onboarding-refresh'))
    } catch (e: any) {
      setError(e?.message || 'Error generando flyer')
    } finally {
      setGenerating(false)
    }
  }

  useEffect(() => {
    if (!overlayFields.length) return

    setOverlayValues((prev) => {
      const next = { ...prev }

      overlayFields.forEach((field: any) => {
        if (next[field.key] === undefined) {
          next[field.key] = ''
        }
      })

      return next
    })
  }, [overlayFields])

  useEffect(() => {
    if (!autoModeFromPayload) return
    if (loadingBootstrap) return
    if (autoStartedRef.current) return
    if (error) return

    const templateId = Number(configuredTemplateId || selectedTemplateId || 0)
    const portraitId = Number(configuredPortraitId || primaryPortrait?.portrait_id || selectedPortraitId || 0)

    if (!templateId || !portraitId) return

    autoStartedRef.current = true
    setMode('auto')

    void generateFlyer({
      templateId,
      portraitId,
      source: 'task_flow_flyer_auto',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoModeFromPayload,
    loadingBootstrap,
    error,
    configuredTemplateId,
    configuredPortraitId,
    selectedTemplateId,
    selectedPortraitId,
    primaryPortrait?.portrait_id,
  ])

  function closeFlow() {
    window.dispatchEvent(new CustomEvent('dashboard-onboarding-refresh'))
    resetTaskFlow()
  }

  const hasOverlayFields = overlayFields.length > 0

  return (
    <FlowShell>
      <div className="shrink-0 border-b border-slate-200 bg-white/90 px-6 py-5 backdrop-blur dark:border-cyan-400/10 dark:bg-[#030712]/90">
        <div className="text-[11px] uppercase tracking-[0.28em] text-cyan-700/70 dark:text-cyan-300/60">
          SYN Flyer Runtime
        </div>

        <div className="mt-2 text-2xl font-semibold">
          Crear flyer
        </div>

        <div className="mt-2 text-sm text-slate-600 dark:text-cyan-100/60">
          Usa tu retrato profesional y un template para generar una pieza premium.
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-10">
        {error ? (
          <div className="mb-5 rounded-2xl border border-rose-500/20 bg-rose-500/8 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </div>
        ) : null}

        <AnimatePresence mode="wait">
          {loadingBootstrap ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <LoadingRuntime />
            </motion.div>
          ) : generating || (mode === 'auto' && !flyerResult && !error) ? (
            <motion.div
              key="generating"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <LoadingRuntime />
            </motion.div>
          ) : flyerResult ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <StepCard
                eyebrow="Bienvenida"
                title="¡Bienvenido a esta nueva etapa!"
                subtitle="Creamos un flyer especial para celebrar tu llegada. Úsalo para compartir tu inicio, inspirar a tu comunidad y presentar con orgullo este nuevo paso."
              >
                <div
                  className={
                    hasOverlayFields
                      ? 'grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start'
                      : 'flex w-full justify-center'
                  }
                >
                  <div className="w-full max-w-[420px]">
                    <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-slate-50 shadow-[0_24px_80px_rgba(15,23,42,0.14)] dark:border-cyan-400/14 dark:bg-[#07101d]">
                      <div className="relative aspect-[9/16] w-full">
                        {flyerResult.image_url ? (
                          <img
                            src={finalRenderedUrl || flyerResult.image_url}
                            alt="Flyer de bienvenida"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-500 dark:text-cyan-100/45">
                            <ImageIcon className="h-10 w-10" />
                          </div>
                        )}

                        {!finalRenderedUrl &&
                          overlayFields.map((field) => (
                            <div
                              key={field.key}
                              style={{
                                position: 'absolute',
                                left: `${field.x}%`,
                                top: `${field.y}%`,
                                width: `${field.width}%`,
                                transform: `translate(-50%, -50%) rotate(${
                                  field.rotation || 0
                                }deg)`,
                                fontSize: `${field.fontSize}px`,
                                fontFamily: field.fontFamily,
                                fontWeight: field.fontWeight,
                                color: field.color,
                                textAlign: field.align as any,
                                lineHeight: 1.05,
                                pointerEvents: 'none',
                                textShadow: '0 2px 10px rgba(0,0,0,0.35)',
                              }}
                            >
                              {renderOverlayText(overlayValues[field.key], field)}
                            </div>
                          ))}

                        <div className="absolute left-3 top-3 rounded-full border border-emerald-400/25 bg-emerald-400/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-emerald-100">
                          Listo
                        </div>
                      </div>
                    </div>
                  </div>

                  {hasOverlayFields ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-cyan-50">
                          Personaliza tus datos
                        </h3>

                        <p className="mt-1 text-sm text-slate-500 dark:text-cyan-100/55">
                          Estos textos se colocarán automáticamente en el diseño final.
                        </p>
                      </div>

                      <div className="grid gap-3">
                        {overlayFields.map((field: any) => (
                          <div key={field.id || field.key}>
                            <label className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-cyan-300/55">
                              {field.label || field.key}
                            </label>

                            <input
                              value={overlayValues[field.key] || ''}
                              onChange={(e) => {
                                setFinalRenderedUrl(null)
                                setSavedFinalUrl(null)
                                setShowShareOptions(false)

                                setOverlayValues((prev) => ({
                                  ...prev,
                                  [field.key]: e.target.value,
                                }))
                              }}
                              placeholder={field.label || field.key}
                              className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-cyan-400 dark:border-cyan-400/14 dark:bg-cyan-400/[0.04] dark:text-cyan-50"
                            />
                          </div>
                        ))}
                      </div>

                      {finalRenderedUrl ? (
                        <button
                          type="button"
                          onClick={() => {
                            setFinalRenderedUrl(null)
                            setShowShareOptions(false)
                          }}
                          className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200"
                        >
                          Cambiar textos de nuevo
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mt-8 flex flex-wrap justify-center gap-3 border-t border-slate-200 pt-5 dark:border-cyan-400/10">
                  <button
                    type="button"
                    onClick={renderFinalFlyerWithOverlay}
                    disabled={renderingFinal}
                    className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                  >
                    {renderingFinal ? 'Renderizando...' : finalRenderedUrl ? 'Regenerar flyer final' : 'Generar flyer final'}
                  </button>

                  {finalRenderedUrl ? (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={async () => {
                          const url = await saveFinalRenderedFlyer()
                          if (url) setShowShareOptions((prev) => !prev)
                        }}
                        disabled={savingFinal || !finalRenderedUrl}
                        className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                      >
                        {savingFinal ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BadgeCheck className="h-4 w-4" />
                        )}
                        {savingFinal ? 'Guardando...' : 'Compartir'}
                      </button>

                      {showShareOptions ? (
                        <div className="absolute bottom-full left-1/2 z-30 mb-3 w-56 -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-cyan-400/20 dark:bg-[#07101d]">
                          <button
                            type="button"
                            onClick={shareToFacebook}
                            className="w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-cyan-100 dark:hover:bg-cyan-400/10"
                          >
                            Facebook
                          </button>

                          <button
                            type="button"
                            onClick={shareToWhatsApp}
                            className="w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-cyan-100 dark:hover:bg-cyan-400/10"
                          >
                            WhatsApp
                          </button>

                          <button
                            type="button"
                            onClick={copyFlyerLink}
                            className="w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-cyan-100 dark:hover:bg-cyan-400/10"
                          >
                            Copiar link
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={closeFlow}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Finalizar
                  </button>
                </div>
              </StepCard>
            </motion.div>
          ) : error && mode === 'auto' ? (
            <motion.div
              key="auto-error"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <StepCard
                eyebrow="Créditos insuficientes"
                title="No pudimos crear tu flyer"
                subtitle="Necesitas créditos disponibles para generar este flyer automático."
              >
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={closeFlow}
                    className="rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950"
                  >
                    Entendido
                  </button>
                </div>
              </StepCard>
            </motion.div>
          ) : (
            <motion.div
              key={`manual-${manualStep}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="space-y-6"
            >
              {manualStep === 1 && (
                <StepCard
                  eyebrow="Paso 1"
                  title="Elige tu flyer"
                  subtitle="Selecciona el template que quieres usar como base visual."
                >
                  <div className="space-y-5">
                    <div className="space-y-3">
                      <input
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Buscar template..."
                        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none transition focus:border-cyan-400 dark:border-cyan-400/14 dark:bg-cyan-400/[0.04] dark:text-cyan-50"
                      />

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedLabelFilter('all')}
                          className={cn(
                            'rounded-full border px-3 py-1.5 text-sm transition',
                            selectedLabelFilter === 'all'
                              ? 'border-slate-900 bg-slate-100 text-slate-900 dark:border-cyan-300/60 dark:bg-cyan-300/14 dark:text-cyan-50'
                              : 'border-slate-200 text-slate-500 hover:border-slate-400 dark:border-cyan-400/14 dark:text-cyan-100/60'
                          )}
                        >
                          Todos
                        </button>

                        {templateLabels.map((label) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() =>
                              setSelectedLabelFilter((prev) => (prev === label ? 'all' : label))
                            }
                            className={cn(
                              'rounded-full border px-3 py-1.5 text-sm transition',
                              selectedLabelFilter === label
                                ? 'border-slate-900 bg-slate-100 text-slate-900 dark:border-cyan-300/60 dark:bg-cyan-300/14 dark:text-cyan-50'
                                : 'border-slate-200 text-slate-500 hover:border-slate-400 dark:border-cyan-400/14 dark:text-cyan-100/60'
                            )}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {filteredTemplates.map((template: any) => {
                        const active = Number(selectedTemplateId) === Number(template.template_id)

                        return (
                          <button
                            key={template.template_id}
                            type="button"
                            onClick={() => handleTemplateSelect(template.template_id)}
                            className={cn(
                              'group overflow-hidden rounded-[24px] border bg-white text-left transition-all dark:bg-cyan-400/[0.04]',
                              active
                                ? 'border-2 border-slate-900 shadow-sm dark:border-cyan-300/60'
                                : 'border-slate-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-sm dark:border-cyan-400/14 dark:hover:border-cyan-300/25'
                            )}
                          >
                            <div className="relative aspect-[9/16] w-full overflow-hidden bg-slate-100 dark:bg-cyan-400/10">
                              {template.base_image_url ? (
                                <img
                                  src={template.base_image_url}
                                  alt={template.name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-slate-400">
                                  <ImageIcon className="h-8 w-8" />
                                </div>
                              )}

                              {active ? (
                                <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium shadow-sm dark:bg-[#07101d]/90">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                                  Seleccionado
                                </div>
                              ) : null}
                            </div>

                            <div className="space-y-1 p-3.5">
                              <div className="font-medium text-slate-900 dark:text-cyan-50">
                                {template.name}
                              </div>

                              <div className="line-clamp-2 text-xs text-slate-500 dark:text-cyan-100/50">
                                {template.description || 'Sin descripción'}
                              </div>

                              {template.label ? (
                                <div className="mt-2 inline-flex items-center rounded-full bg-cyan-500/10 px-2.5 py-1 text-[11px] font-medium text-cyan-700 dark:text-cyan-300">
                                  {template.label}
                                </div>
                              ) : null}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </StepCard>
              )}

              {manualStep === 2 && (
                <StepCard
                  eyebrow="Paso 2"
                  title="Elige tu retrato"
                  subtitle="Selecciona el retrato profesional que quieres integrar en el flyer."
                >
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setManualStep(1)}
                        className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200"
                      >
                        Atrás
                      </button>

                      <div className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-500 dark:border-cyan-400/14 dark:text-cyan-100/55">
                        {portraits.length} retrato{portraits.length === 1 ? '' : 's'}
                      </div>
                    </div>

                    {portraits.length > 0 ? (
                      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-4">
                        {portraits.map((portrait) => {
                          const active = Number(selectedPortraitId) === Number(portrait.portrait_id)

                          return (
                            <button
                              key={portrait.portrait_id}
                              type="button"
                              onClick={() => handlePortraitSelect(portrait.portrait_id)}
                              className={cn(
                                'group relative overflow-hidden rounded-[24px] border bg-white text-left transition-all dark:bg-cyan-400/[0.04]',
                                active
                                  ? 'border-2 border-slate-900 shadow-sm dark:border-cyan-300/60'
                                  : 'border-slate-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-sm dark:border-cyan-400/14 dark:hover:border-cyan-300/25'
                              )}
                            >
                              <div className="aspect-[4/5] w-full overflow-hidden bg-slate-100 dark:bg-cyan-400/10">
                                {portrait.image_url ? (
                                  <img
                                    src={portrait.image_url}
                                    alt={`Portrait ${portrait.portrait_id}`}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                                    <ImageIcon className="h-7 w-7" />
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center justify-between gap-2 p-2.5">
                                {Number(portrait.is_primary || 0) === 1 ? (
                                  <div className="inline-flex items-center gap-1 rounded-full bg-cyan-500/10 px-2 py-1 text-[11px] font-medium text-cyan-700 dark:text-cyan-300">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Principal
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-500 dark:text-cyan-100/50">
                                    Retrato
                                  </span>
                                )}

                                {active ? (
                                  <span className="text-[11px] font-medium text-cyan-600 dark:text-cyan-300">
                                    Activo
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="rounded-3xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-cyan-400/14 dark:text-cyan-100/50">
                        Todavía no tienes retratos. Primero crea tu retrato profesional.
                      </div>
                    )}
                  </div>
                </StepCard>
              )}

              {manualStep === 3 && (
                <StepCard
                  eyebrow="Paso 3"
                  title="Confirma la composición"
                  subtitle="Revisa el template y el retrato seleccionados antes de generar tu flyer."
                >
                  <div className="space-y-6">
                    <div className="relative mx-auto h-[560px] w-full max-w-[520px]">
                      <div className="absolute left-1/2 top-6 w-[250px] -translate-x-1/2 rounded-[26px] bg-white p-3 shadow-[0_18px_60px_rgba(0,0,0,0.12)] dark:bg-[#07101d]">
                        <div className="overflow-hidden rounded-[20px] bg-slate-100 aspect-[9/16] dark:bg-cyan-400/10">
                          {selectedTemplate?.base_image_url ? (
                            <img
                              src={selectedTemplate.base_image_url}
                              alt={selectedTemplate.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-slate-400">
                              <ImageIcon className="h-8 w-8" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="absolute left-1/2 top-44 w-[220px] -translate-x-[8%] rotate-[8deg] rounded-[24px] bg-white p-3 shadow-[0_18px_60px_rgba(0,0,0,0.16)] dark:bg-[#07101d]">
                        <div className="overflow-hidden rounded-[18px] bg-slate-100 aspect-[4/5] dark:bg-cyan-400/10">
                          {selectedPortrait?.image_url ? (
                            <img
                              src={selectedPortrait.image_url}
                              alt="Retrato"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-slate-400">
                              <ImageIcon className="h-7 w-7" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mx-auto grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 dark:border-cyan-400/10 dark:bg-cyan-400/[0.04]">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-cyan-300/55">
                          Template
                        </div>
                        <div className="mt-2 text-sm text-slate-900 dark:text-cyan-50">
                          {selectedTemplate?.name || '—'}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 dark:border-cyan-400/10 dark:bg-cyan-400/[0.04]">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-cyan-300/55">
                          Retrato
                        </div>
                        <div className="mt-2 text-sm text-slate-900 dark:text-cyan-50">
                          {selectedPortrait
                            ? Number(selectedPortrait.is_primary || 0) === 1
                              ? 'Retrato principal'
                              : 'Retrato seleccionado'
                            : '—'}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-center gap-3">
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setManualStep(2)}
                          className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm text-slate-700 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200"
                        >
                          Atrás
                        </button>

                        <button
                          type="button"
                          onClick={() => void generateFlyer({ source: 'task_flow_flyer_manual' })}
                          disabled={!selectedTemplateId || !selectedPortraitId || generating}
                          className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                        >
                          {generating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                          {generating ? 'Generando...' : 'Crear flyer'}
                        </button>
                      </div>
                    </div>
                  </div>
                </StepCard>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FlowShell>
  )
}